// @ts-strict-ignore
import React, { useContext } from 'react';
import type { TFunction } from 'i18next';

import generateVersionChangelog, { formatters } from 'flow/service/changelog';
import {
  ButtonVariant, IconName, useSnackBar, Text, Icon, Button,
} from '@usealloy/component-library';
import { StyledChangelogCardContainer, StyledChangelogCardContentWrapper, StyledFadedText } from './styles';
import { useAppSelector } from '~/store/reduxHooks';
import {
  selectInitialWorkflowData,
  selectInitialWorkflowProps,
  selectPublicVersion,
  selectWorkflowData,
  selectWorkflowProps,
} from '~/dashboard/views/workflows/store/redux/selectors';
import { withI18n } from '~/helpers/i18n';
import { captureExceptionsToSentry } from '~/helpers/exceptionHandler';
import { UserContext } from '~/dashboard/context';
import type { SchemaInstance, WorkflowProps } from 'flow/store/engine/types';


interface ChangelogCardProps {
  t: TFunction;
  localWorkflowData?: Partial<SchemaInstance>;
  localWorkflowProps?: WorkflowProps;
}
function ChangelogCard({ t, localWorkflowData, localWorkflowProps }: ChangelogCardProps) {
  const workflowProps = useAppSelector(selectWorkflowProps);
  const workflowData = useAppSelector(selectWorkflowData);
  const initialWorkflowData = useAppSelector(selectInitialWorkflowData);
  const initialWorkflowProps = useAppSelector(selectInitialWorkflowProps);
  const publicVersion = useAppSelector(selectPublicVersion);

  const addSnackBar = useSnackBar();

  const { featureFlags } = useContext(UserContext);


  const changelog = React.useMemo(() => {
    try {
      return generateVersionChangelog({
        A: {
          props: initialWorkflowProps,
          version: initialWorkflowData,
        },
        B: {
          props: localWorkflowProps || workflowProps,
          version: localWorkflowData || workflowData,
        },
        formatter: formatters.prettifiedFormatter,
        featureFlags,
      });
    } catch (error) {
      captureExceptionsToSentry(error);
      addSnackBar({
        title: t('features.workflow.saveVersionModal.changelog.generatingError'),
        type: 'danger',
        duration: 3_000,
      });
      return undefined;
    }
  }, [addSnackBar, initialWorkflowData, initialWorkflowProps, t, workflowData, workflowProps,
    featureFlags, localWorkflowData, localWorkflowProps]);

  const handleChangelogCopy = React.useCallback(async () => {
    try {
      const { content: changelogAsMarkdown } = generateVersionChangelog({
        A: {
          props: initialWorkflowProps,
          version: initialWorkflowData,
        },
        B: {
          props: localWorkflowProps || workflowProps,
          version: localWorkflowData || workflowData,
        },
        formatter: formatters.markdownFormatter,
        featureFlags,
      });
      await navigator.clipboard.writeText(changelogAsMarkdown);
      addSnackBar({
        title: t('features.workflow.saveVersionModal.changelog.copySuccess'),
        type: 'success',
        duration: 3_000,
      });
    } catch (error) {
      captureExceptionsToSentry(error);
      addSnackBar({
        title: t('features.workflow.saveVersionModal.changelog.copyError'),
        type: 'danger',
        duration: 3_000,
      });
    }
  }, [addSnackBar, initialWorkflowData, initialWorkflowProps, t, workflowData, workflowProps,
    featureFlags, localWorkflowData, localWorkflowProps]);

  return (
    <StyledChangelogCardContainer
      collapsible
      header={(
        <div data-dd-action-name="save-modal-changelog-detail">
          <Text>Changes from V{publicVersion} ({changelog?.changesCount ?? 'N/A'})</Text>
        </div>
      )}
      headerButtons={[
        <Button
          key="change-copy-button"
          variant={ButtonVariant.tertiary}
          disabled={changelog ? changelog.changesCount === 0 : true}
          onClick={handleChangelogCopy}
        >
          <Icon icon={IconName.Copy} />
        </Button>,
      ]}
    >
      <StyledChangelogCardContentWrapper isEmpty={changelog?.changesCount === 0}>
        {/* these 3 conditions are mutually exclusive */}
        {(changelog && changelog.changesCount > 0) && changelog.content}
        {(changelog && changelog.changesCount === 0) && (
          <StyledFadedText>
            {t('features.workflow.saveVersionModal.changelog.noChangesInVersion') as unknown as React.ReactNode}
          </StyledFadedText>
        )}
        {(!changelog && (
          <StyledFadedText>
            {t('features.workflow.saveVersionModal.changelog.generatingError') as unknown as React.ReactNode}
          </StyledFadedText>
        ))}
      </StyledChangelogCardContentWrapper>
    </StyledChangelogCardContainer>
  );
}

export default withI18n({
  importPath: 'features/workflow',
  translationKey: 'features.workflow.saveVersionModal',
})(ChangelogCard);
